/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/ui/sonner";
import { Loader2, ExternalLink, Sparkles, Link2, Save, FileSignature, GitMerge } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { LeadFields, LeadContactsEditor, leadFormFromLead, leadBodyFromForm } from "../../../components/LeadFields";
import { fmtT, fmtD, isClosedProject } from "./meta";
import { useCrmMeta, formFromProject } from "./customerForm";
import { T } from "../../../lib/i18n";
/* 【2026-10-29 时区统一】签约日上限按美国日历日取「今天」（原来是 UTC 日期）。 */
import { usToday } from "../../../lib/usDate";
/* 【2026-10-30 输入侧时区提示】签约日是设计部的关键基准（标准结束日 = 签约日 + 82
   美国工作日），中国同事按北京日期填会直接让整条基线差一天，所以这个输入框必须有提示。 */
import { UsDateHint } from "./UsDateHint";

/* 「客户 / 项目信息」：与销售部「新增客户线索 / 编辑客户信息」完全同一套表单（LeadFields）。已关联线索 → 直接编辑该线索并镜像到项目；未关联 → 保存时在 CRM 新建线索并关联。 */
export const TYPE_ZH = { detached_adu: "独立 ADU", attached_adu: "附属 ADU", garage_conversion: "车库改造", junior_adu: "Junior ADU", two_story_adu: "二层 ADU", addition: "加建", new_construction: "新建", remodel: "改造", fire_rebuild: "火灾重建" };
export const typeLabel = (k) => (k ? T(TYPE_ZH[k] || k) : "");
const VIA_ZH = { manual: "手工新增", csv_import: "CSV 导入", referral: "老客户推荐", public_form: "在线表单", design: "设计部登记" };
const SRC_ZH = { manual: "经理设置", contract: "自动识别 · CRM 合同在线签署时间", lead_signed: "自动识别 · 签字版合同时间", project: "项目记录的合同签订日（建项时填写）", lead_date: "自动识别 · CRM 线索签约日", eng_project: "自动识别 · 工程项目签约日", lead_stage: "估算 · 线索进入「已签约」的时间（非合同签订日）", created: "估算 · 按建项日期" };
const APPROVAL_ZH = { sequential: "分开提交：先 Planning，批准后再 Building", concurrent: "同步提交：Planning + Building 同时提交、同步审查" };
const today = () => usToday();

/* 合同签署日期：未确定 → 设计经理直接设置；已确定（CRM 自动识别 / 经理设置过）→ 设计经理申请 + 销售 VP 审批；admin / CEO / 销售 VP 可直接改。 */
const SignDateSection = ({ p, api, role, onChanged }) => {
  const [si, setSi] = useState(p.signInfo || null);
  const [v, setV] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setSi(p.signInfo || null), [p.signInfo]);
  const isMgr = ["design_manager", "admin", "ceo"].includes(role);
  const isExec = ["admin", "ceo", "sales_vp"].includes(role);
  if (!si) return null;
  const direct = async () => {
    if (!v) return toast.error(T("请选择日期"));
    setBusy(true);
    try { const { data } = await api.patch(`/design/v3/projects/${p.id}`, { contractSignDate: v }); setSi(data.signInfo || si); setV(""); toast.success(T("合同签署日期已设为 {0}", [fmtD(v)])); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const request = async () => {
    if (!v) return toast.error(T("请选择新日期"));
    if (reason.trim().length < 2) return toast.error(T("请填写修改理由（≥2 字）"));
    setBusy(true);
    try { const { data } = await api.post(`/design/v3/projects/${p.id}/sign-date-request`, { date: v, reason: reason.trim() }); setSi({ ...si, pendingChange: { id: data.approval.id, toDate: v, reason: reason.trim(), requestedBy: data.approval.requestedBy, at: data.approval.at } }); setV(""); setReason(""); toast.success(T("已提交，待销售 VP / CEO 审批")); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const inp = "border border-slate-300 rounded-md px-2 py-1 text-xs bg-white";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs space-y-1.5" data-testid="dv3-info-sign" data-locked={si.locked ? "1" : "0"} data-pending={si.pendingChange ? "1" : "0"} data-source={si.source}>
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature className="h-3.5 w-3.5 text-slate-400" /><span className="font-semibold text-slate-700">{T("合同签署日期")}</span>
        <b className="text-slate-900" data-testid="dv3-info-sign-date">{fmtD(si.date)}</b>
        <span className="text-[10px] text-slate-400" data-testid="dv3-info-sign-source">{T(SRC_ZH[si.source] || si.source)}</span>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${si.locked ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="dv3-info-sign-lock">{si.locked ? T("已确定 · 修改需销售 VP 审批") : T("未确定 · 设计经理可直接设置")}</span>
      </div>
      {si.pendingChange && (
        <p className="text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded-md px-2 py-1" data-testid="dv3-info-sign-pending">{T("修改申请待审批：{0} → {1} · {2} · {3}", [fmtD(si.date), fmtD(si.pendingChange.toDate), si.pendingChange.requestedBy, si.pendingChange.reason])}</p>
      )}
      {!si.pendingChange && !isClosedProject(p) && (
        !si.locked ? (isMgr || isExec) && (
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" max={today()} value={v} onChange={(e) => setV(e.target.value)} className={inp} data-testid="dv3-info-sign-input" />
            <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-700 text-white" disabled={busy || !v} onClick={direct} data-testid="dv3-info-sign-set">{T("设置签署日期")}</Button>
            <span className="text-[10px] text-slate-400">{T("设置后即为「已确定」，之后修改需销售 VP 审批")}</span>
          </div>
        ) : isExec ? (
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" max={today()} value={v} onChange={(e) => setV(e.target.value)} className={inp} data-testid="dv3-info-sign-input" />
            <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-700 text-white" disabled={busy || !v} onClick={direct} data-testid="dv3-info-sign-set">{T("直接修改（VP / CEO）")}</Button>
          </div>
        ) : isMgr ? (
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" max={today()} value={v} onChange={(e) => setV(e.target.value)} className={inp} data-testid="dv3-info-sign-input" />
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={T("修改理由（必填）")} className={`${inp} flex-1 min-w-[160px]`} data-testid="dv3-info-sign-reason" />
            <Button size="sm" variant="outline" className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50" disabled={busy || !v} onClick={request} data-testid="dv3-info-sign-request">{T("申请修改 → 销售 VP 审批")}</Button>
          </div>
        ) : <p className="text-[10px] text-slate-400">{T("签署日期由设计经理申请、销售 VP 审批修改")}</p>
      )}
      {/* 【2026-10-30】三个可编辑分支（未锁直设 / VP 直改 / 经理申请改）都要求 isMgr||isExec，
          所以提示挂在这一个条件上，避免在三处重复写同一行。 */}
      {(isMgr || isExec) && !si.pendingChange && !isClosedProject(p) && <UsDateHint testid="dv3-info-sign-tz" />}
    </div>
  );
};

/* 审批模式：分开提交（先 Planning 后 Building）/ 同步提交（P+B 同步审查，阶段合并显示但任务不减）——建项时选择、后期可改 */
const ApprovalModeSection = ({ p, api, canEdit, onChanged }) => {
  const [mode, setMode] = useState(p.approvalMode || "sequential");
  const [busy, setBusy] = useState(false);
  const pick = async (m) => {
    if (m === mode) return;
    setBusy(true);
    try { await api.patch(`/design/v3/projects/${p.id}`, { approvalMode: m }); setMode(m); toast.success(T("审批模式已切换为「{0}」· 阶段任务不变，仅显示合并", [T(APPROVAL_ZH[m])])); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs space-y-1.5" data-testid="dv3-info-approval-mode" data-mode={mode}>
      <div className="flex flex-wrap items-center gap-2">
        <GitMerge className="h-3.5 w-3.5 text-teal-600" /><span className="font-semibold text-slate-700">{T("审批 / 提交类型")}</span>
        {canEdit ? (
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-[11px]">
            <button type="button" disabled={busy} data-testid="dv3-info-approval-sequential" aria-pressed={mode === "sequential"} onClick={() => pick("sequential")} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2.5 py-1 rounded-md font-semibold ${mode === "sequential" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"}`)}>{T("分开提交 · 先 Planning 后 Building")}</button>
            <button type="button" disabled={busy} data-testid="dv3-info-approval-concurrent" aria-pressed={mode === "concurrent"} onClick={() => pick("concurrent")} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2.5 py-1 rounded-md font-semibold ${mode === "concurrent" ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-white"}`)}>{T("同步提交 · P+B 同步审查")}</button>
          </div>
        ) : <span className="text-slate-600">{T(APPROVAL_ZH[mode])}</span>}
      </div>
      <p className="text-[10px] text-slate-400">{T("按项目所在城市规则选择：同步提交时 Planning / Building 对应阶段在阶段任务里合并为一个节点显示，但底层阶段与子任务不变、仍需全部完成。")}</p>
    </div>
  );
};

export const ProjectInfoDialog = ({ p, api, onClose, onSaved, onChanged }) => {
  const nav = useNavigate();
  const { lang, user } = useApp();
  const zh = lang === "zh";
  const meta = useCrmMeta();
  const [cust, setCust] = useState(null);
  const [form, setForm] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detecting, setDetecting] = useState(false);
  useEffect(() => { api.get(`/design/v3/projects/${p.id}/customer`).then(({ data }) => setCust(data)).catch((e) => setCust({ error: e?.detailText || e.message })); }, [api, p.id]);
  useEffect(() => {
    if (!meta || !cust || cust.error || form) return;
    setForm(cust.lead ? leadFormFromLead(cust.lead, meta) : formFromProject(cust.project, meta));
    setContacts((cust.lead?.contacts || []).map((c) => ({ ...c })));
  }, [meta, cust]); // eslint-disable-line
  const detect = async () => {
    setDetecting(true);
    try { const { data } = await api.get(`/design/v3/projects/${p.id}/project-type/detect`); if (data.type) { setForm((f) => ({ ...f, projectType: data.type })); toast.success(T("识别结果：{0}", [typeLabel(data.type)])); } else toast.message(T("未能从 CRM 记录识别出项目类型")); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setDetecting(false);
  };
  const save = async () => {
    if (!form.firstName.trim() && !form.lastName.trim()) return toast.error(zh ? "请填写客户姓名" : "Name required");
    if (!form.branch) return toast.error(zh ? "请选择所属分公司" : "Branch required");
    if (!form.address.trim()) return toast.error(T("请填写项目地址"));
    setBusy(true);
    try {
      const { data } = await api.put(`/design/v3/projects/${p.id}/customer`, { ...leadBodyFromForm(form), contacts: contacts.filter((c) => `${c.name || ""}${c.phone || ""}${c.email || ""}`.trim()) });
      toast.success(data.created ? T("已保存 · 已在 CRM 新建客户线索并关联") : T("已保存 · 已同步 CRM 线索"));
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const lead = cust?.lead;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto overflow-x-hidden z-[95]" overlayClassName="z-[94]" data-testid="dv3-info-dialog">
        <DialogHeader>
          <DialogTitle>{T("客户 / 项目信息")}</DialogTitle>
          <DialogDescription data-testid="dv3-info-sub">{p.projectId} · {lead ? T("CRM 线索 {0} · 字段与销售部「编辑客户信息」一致，保存后双向同步", [lead.clientName]) : T("未关联 CRM 线索 · 字段与销售部「新增客户线索」一致，保存时将在 CRM 新建线索并关联")}</DialogDescription>
        </DialogHeader>
        {cust?.error ? <p className="text-sm text-red-600" data-testid="dv3-info-error">{cust.error}</p>
          : !form || !meta ? <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          : <>
            {lead && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-xs text-slate-600 grid sm:grid-cols-3 gap-x-4 gap-y-1" data-testid="dv3-info-provenance">
                <div><span className="text-slate-400">{zh ? "创建人" : "Created by"}</span><p className="font-semibold text-slate-800 truncate">{lead.createdBy || lead.importedBy || (zh ? "未记录" : "—")}</p></div>
                <div><span className="text-slate-400">{zh ? "创建时间" : "Created at"}</span><p className="font-semibold text-slate-800">{fmtT(lead.createdAt)}</p></div>
                <div><span className="text-slate-400">{zh ? "登记方式 / 来源" : "Channel / Source"}</span><p className="font-semibold text-slate-800 truncate">{T(VIA_ZH[lead.createdVia] || "手工新增")}{lead.leadSource ? <span className="text-slate-400 font-normal"> · {lead.leadSource}</span> : null}</p></div>
              </div>
            )}
            <LeadFields form={form} setForm={setForm} meta={meta} zh={zh} tid="dv3-info" />
            <SignDateSection p={p} api={api} role={user?.role} onChanged={onChanged} />
            {!isClosedProject(p) && <ApprovalModeSection p={p} api={api} canEdit={["design_manager", "admin", "ceo", "cn_architect", "us_policy"].includes(user?.role) || (user?.role || "").startsWith("role_")} onChanged={onChanged} />}
            <LeadContactsEditor contacts={contacts} setContacts={setContacts} form={form} setForm={setForm} zh={zh} tid="dv3-info" />
            <div className="flex flex-wrap items-center gap-2 pt-2">
              {lead && <button type="button" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs text-violet-700 hover:underline inline-flex items-center gap-1" data-testid="dv3-info-lead" onClick={() => nav(`/crm/leads/${lead.id}`)}><ExternalLink className="h-3 w-3" />{T("打开 CRM 线索")}</button>}
              {lead && <Button size="sm" variant="outline" className="h-7 text-xs border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="dv3-info-detect" disabled={detecting} onClick={detect}>{detecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}{T("按 CRM 记录识别项目类型")}</Button>}
              {!lead && <span className="text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="dv3-info-will-create"><Link2 className="h-3 w-3" />{T("保存后将新建 CRM 线索（阶段按表单，默认已签约）")}</span>}
              <div className="flex-wrap md:flex-nowrap ml-auto flex gap-2">
                <Button variant="outline" size="sm" data-testid="dv3-info-cancel" onClick={onClose}>{T("取消")}</Button>
                <Button size="sm" data-testid="dv3-info-save" onClick={save} disabled={busy || !cust?.canEdit} className="bg-indigo-600 hover:bg-indigo-700 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />{T("保存")}</>}</Button>
              </div>
            </div>
          </>}
      </DialogContent>
    </Dialog>
  );
};
